import Edit16 from '@carbon/icons-react/es/edit/16';
import { Space, Tooltip } from 'antd';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router';
import styled from 'styled-components';
import { FieldSectionType } from 'api/field-sections/types';
import { Field } from 'api/tasks';
import { Menu } from 'components/atoms/dropdown';
import { IconShell } from 'components/atoms/icon-shell';
import { MoreActionsDropdown } from 'components/atoms/more-actions';
import { Switch } from 'components/atoms/switch/switch';
import { fieldIcon } from 'components/organisms/module-settings/fields/field';
import { Text02 } from 'components/styles';
import { useDeleteConfirmModal } from 'hooks/use-delete-confirm-modal';
import { useTranslation } from 'hooks/use-translation';
import { useDeleteFieldMutation, useUpdateFieldMutation } from 'queries';
import {
  getProjectFieldEditUrl,
  getTaskFieldEditUrl,
  getTeamMemberFieldEditUrl,
} from 'utils/urls';
import {
  canDeleteField,
  canDisableField,
  canEditField,
  getType,
} from '../utils';

const Text = styled.div`
  &,
  & svg {
    ${Text02};
  }
`;

const FieldTypeIconShell = styled(IconShell)`
  margin-left: -8px;
`;

export const FieldType = ({ field }: { field: Field }): JSX.Element => {
  const type = getType(field);

  return (
    <Space size={0}>
      <Text>
        <FieldTypeIconShell size="xsmall" icon={fieldIcon(type)} />
      </Text>
      <Text>
        <FormattedMessage id={`settings.fields.type.${type}`} />
      </Text>
    </Space>
  );
};

export const FieldStatus = ({
  value,
  field,
}: {
  value: boolean;
  field: Field;
}): JSX.Element => {
  const { fieldId, isEnabled } = field;
  const disabled = !canDisableField(field);
  const { mutate, isLoading } = useUpdateFieldMutation(fieldId);
  const disabledText = useTranslation('settings.fields.field.status.disabled');

  return (
    <Space size="small">
      <Tooltip mouseEnterDelay={1} title={disabled ? disabledText : null}>
        <Switch
          size="small"
          disabled={disabled}
          loading={isLoading}
          checked={value}
          onClick={() => mutate({ isEnabled: !value })}
        />
      </Tooltip>
      <Text>
        <FormattedMessage id={isEnabled ? 'enabled' : 'disabled'} />
      </Text>
    </Space>
  );
};

export const MoreOption = ({ field }: { field: Field }): JSX.Element | null => {
  const { fieldId, objectType } = field;
  // const { hasAccess: hasDeleteAccess } = useHasAccess({
  //   permissions: ['WRITE'],
  //   resource: 'taskFields',
  //   resourceId: field.fieldId,
  // });

  const editable = canEditField(field);
  const deletable = canDeleteField(field);
  const { mutate } = useDeleteFieldMutation(fieldId);
  const history = useHistory();
  const confirm = useDeleteConfirmModal({
    title: useTranslation('settings.fields.delete.confirm.title'),
    content: useTranslation('settings.fields.delete.confirm.message'),
    onOk: () => mutate({}),
  });

  const menu = (
    <Menu>
      <Menu.Header>
        <FormattedMessage id="more_actions" />
      </Menu.Header>
      <Menu.Item
        key="edit"
        icon={<Edit16 />}
        onClick={() =>
          history.push(
            objectType === FieldSectionType.Project
              ? getProjectFieldEditUrl(fieldId)
              : objectType === FieldSectionType.Task
              ? getTaskFieldEditUrl(fieldId)
              : getTeamMemberFieldEditUrl(fieldId),
          )
        }
      >
        <FormattedMessage id="edit" />
      </Menu.Item>
      {deletable && (
        <Menu.ItemDelete
          data-cy={`${field.fieldLabel?.replaceAll(
            ' ',
            '',
          )}.more-actions.delete`}
          key="delete"
          onClick={() => confirm()}
        />
      )}
    </Menu>
  );

  return editable || deletable ? (
    <MoreActionsDropdown
      dataCyValue={`${field.fieldLabel?.replaceAll(' ', '')}.more-actions`}
      menu={menu}
    />
  ) : null;
};
